<template>
  <div :class="$style.modal">
    <ModalHeader
      data-test-id="PricingCalculatorModal__header"
      :handleClose="handleClose"
      :headerIsClickable="true"
      headerText="back to pricing"
      :showBackArrow="true"
      :showClose="true"
      @headerClicked="handleClose"
    />
    <div :class="$style.modalBody">
      <header :class="$style.costCalculator">
        <HeadingLarge>
          Cost Calculator
        </HeadingLarge>
      </header>
      <main :class="$style.modalContainer">
        <section :class="$style.pricingCalculatorFormContainer">
          <HeadingSmall>
            Tell us about your setup.
          </HeadingSmall>
          <PricingCalculatorForm
            data-test-id="PricingCalculatorModal__form"
            :savedAnswers="savedAnswers"
            @calculateEstimate="handleCalculateEstimate"
          />
        </section>
        <aside
          v-if="calculateEstimateSubmitted"
          :class="$style.sidepanel"
          data-test-id="PricingCalculator__resultsPanel"
        >
          <div :class="$style.suggestedPlan">
            <HeadingSmall>
              Suggested Plan
            </HeadingSmall>
            <AnnualMonthlyToggle
              name="PricingCalculator"
              :suggestedPlan="planDetails.displayName"
              @change="triggerAnnualMonthlyToggleEvent"
            />
            <SuggestedPlan
              :answers="answers"
              :isAnnual="isAnnual"
            />
          </div>
          <div
            v-if="planDetails"
            :class="$style.pricingComparison"
          >
            <HeadingSmall>
              Pricing Comparison
            </HeadingSmall>
            <ParagraphBody
              bodySize="small"
              weight="light"
            >
              Competitor pricing represents an <strong>average price per user</strong>
              based on multiple competitor models.
            </ParagraphBody>
            <PricingComparison
              :planMonthlyPrice="pricePerMonth"
              :planName="planDetails.displayName"
              :productCode="planDetails.productCode"
              :usersCount="answers.usersCount"
            />
          </div>
        </aside>
      </main>
    </div>
  </div>
</template>

<script>
import {
  HeadingLarge,
  HeadingSmall,
  ParagraphBody,
} from '@jumpcloud/ui-components';
import { UiEvents } from '@jumpcloud/ui-events-vue';
import { eventLocations, eventProperties } from 'client/GrowthEngineering/utilities/GrowthConstants';
import { isEmpty } from 'lodash';
import { mapActions, mapGetters } from 'vuex';
import AnnualMonthlyToggle from 'client/GrowthEngineering/components/AnnualMonthlyToggle.vue';
import EventHelper from 'client/GrowthEngineering/EventHelper';
import ModalEventBus from 'client/components/Modal/ModalEventBus';
import ModalHeader from 'client/components/Modal/ModalComponents/ModalHeader.vue';
import PricingCalculatorForm from 'client/Pricing/PricingCalculator/PricingCalculatorForm.vue';
import PricingComparison from 'client/Pricing/PricingCalculator/PricingComparison.vue';
import SuggestedPlan from 'client/Pricing/PricingCalculator/SuggestedPlan.vue';

const pricingCalculatorModule = 'PricingCalculatorModule';
const pricingModule = 'PricingModule';

const {
  description,
  location,
  section,
  text,
} = eventProperties;
const {
  pricingCalculatorModal,
} = eventLocations;

export default {
  name: 'PricingCalculatorModal',

  EventHelper,
  pricingCalculatorModal,

  components: {
    AnnualMonthlyToggle,
    HeadingLarge,
    HeadingSmall,
    ModalHeader,
    ParagraphBody,
    PricingCalculatorForm,
    PricingComparison,
    SuggestedPlan,
  },

  data() {
    return {
      calculateEstimateSubmitted: false,
      answers: {},
    };
  },

  computed: {
    ...mapGetters(pricingCalculatorModule, {
      planDetails: 'getPlanDetails',
      savedAnswers: 'getAnswers',
      suggestedPlan: 'getPlanDetails',
    }),

    ...mapGetters(pricingModule, {
      isAnnual: 'getIsAnnual',
    }),

    pricePerMonth() {
      return this.isAnnual ? this.planDetails.annualPrice : this.planDetails.listPrice;
    },
  },

  mounted() {
    this.handleMount();
  },

  methods: {
    ...mapActions(pricingCalculatorModule,
      [
        'resetAnswers',
        'setAnswers',
      ]),

    handleCalculateEstimate(answers) {
      this.answers = answers;
      this.calculateEstimateSubmitted = true;
      this.setAnswers(answers);

      this.$options.EventHelper.triggerButtonClicked({
        [description]: 'calculate estimate button clicked',
        [location]: pricingCalculatorModal,
        [section]: `Recommended Package: ${this.suggestedPlan ? this.suggestedPlan.displayName : 'No recommendation'}`,
        [text]: 'calculate estimate',
      });
    },

    handleClose() {
      this.resetAnswers();
      ModalEventBus.close();
    },

    handleMount() {
      if (!isEmpty(this.savedAnswers)) {
        this.handleCalculateEstimate(this.savedAnswers);
      }
    },

    triggerAnnualMonthlyToggleEvent(billingType) {
      UiEvents.triggerToggleClicked({
        description: `pricing calculator toggled to: ${billingType}`,
        page: this.$page,
        section: `Suggested Plan: ${this.planDetails.displayName}`,
        text: 'Annual/Monthly',
        value: billingType,
      });
    },
  },
};
</script>

<style module>
@import '../../components/Modal/css/ModalStyles.css';

@value (max-width-large) from '~css/breakpoints.css';

.costCalculator {
  margin-bottom: var(--jc-spacer);
}

.modalContainer {
  display: flex;
}

.pricingCalculatorFormContainer {
  flex: 2 1 0;
}

.pricingComparison {
  margin-top: var(--jc-spacer);
}

.sidepanel {
  flex: 1 1 0;
  margin-left: 4rem;
}

@media (max-width: max-width-large) {
  /* On smaller screens, make the screen 50/50 */
  .pricingCalculatorFormContainer {
    flex: 1 1 0;
  }
}
</style>
